import React, {
  createContext, useContext, useState, useEffect, useCallback,
} from 'react';
import { authAPI } from '../services/api';
import { useIdleTimeout } from '@shared/hooks/useIdleTimeout';
import { SessionTimeoutWarning } from '@shared/components/Auth/SessionTimeoutWarning';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [learner, setLearner] = useState(() => {
    const saved = localStorage.getItem('scenario_os_learner');
    return saved ? JSON.parse(saved) : null;
  });
  const [loading, setLoading] = useState(false);

  const [activeCharacter, setActiveCharacter] = useState(null);
  const [activeSpecialty, setActiveSpecialty] = useState(null);

  const isAuthenticated = !!learner;
  const COACH_ROLES_FE = new Set(['coach_learner', 'coach', 'admin', 'administrator', 'owner', 'platform_admin']);
  const ADMIN_ROLES_FE = new Set(['admin', 'administrator', 'owner', 'platform_admin']);
  const isCoach = COACH_ROLES_FE.has(learner?.role);
  const isAdmin = ADMIN_ROLES_FE.has(learner?.role);
  const isOwner = learner?.role === 'owner' || learner?.role === 'platform_admin';
  const isPlatformAdmin = learner?.role === 'platform_admin';
  // Phase-1A: the LearnerProfile API now returns tenant_id +
  // authoring_domains alongside enrolled_domains.  Surface them through
  // the context so every coach UI page can scope itself with one read
  // instead of refetching /profile.  Defaults keep older bearer tokens
  // (no tenant claim, no authoring_domains list) working.
  const tenantId = learner?.tenant_id || 'scenario_os';
  const enrolledDomains = Array.isArray(learner?.enrolled_domains) ? learner.enrolled_domains : [];
  const authoringDomains = Array.isArray(learner?.authoring_domains) ? learner.authoring_domains : [];
  const domainTiers = learner?.domain_tiers || {};
  const canAuthorDomain = (domainId) => isAdmin || authoringDomains.includes(domainId);
  const isEnrolledIn = (domainId) => enrolledDomains.includes(domainId) || canAuthorDomain(domainId);
  const getTier = (domainId) => {
    if (isAdmin || canAuthorDomain(domainId)) return 'paid';
    const tier = domainTiers[domainId] || 'free';
    return (tier === 'active' || tier === 'trial') ? 'paid' : 'free';
  };
  const isPaidFor = (domainId) => getTier(domainId) === 'paid';

  const logout = useCallback(() => {
    localStorage.removeItem('scenario_os_token');
    localStorage.removeItem('scenario_os_learner');
    setLearner(null);
    setActiveCharacter(null);
    setActiveSpecialty(null);
  }, []);

  const refreshProfile = useCallback(async () => {
    try {
      const { data } = await authAPI.getProfile();
      localStorage.setItem('scenario_os_learner', JSON.stringify(data));
      setLearner(data);
    } catch {
      logout();
    }
  }, [logout]);

  // Re-mint the JWT from the server so domain_tiers are always current.
  // Called on app startup and can be called by pages after a coach tier change.
  const refreshToken = useCallback(async () => {
    try {
      const { data } = await authAPI.refreshToken();
      if (data?.access_token) {
        localStorage.setItem('scenario_os_token', data.access_token);
        // Immediately reload the profile so domain_tiers in React state updates
        await refreshProfile();
      }
    } catch {
      // Non-fatal — the existing token continues to work
    }
  }, [refreshProfile]);

  useEffect(() => {
    const t = localStorage.getItem('scenario_os_token');
    if (t) {
      // Refresh the JWT first so domain_tiers reflect any coach-granted upgrades,
      // then load the profile.
      refreshToken().catch(() => refreshProfile());
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login({ email, password });
    localStorage.setItem('scenario_os_token', data.access_token);
    const profile = await authAPI.getProfile();
    const merged = {
      ...profile.data,
      password_change_required: Boolean(
        profile.data.password_change_required ?? data.password_change_required,
      ),
    };
    localStorage.setItem('scenario_os_learner', JSON.stringify(merged));
    setLearner(merged);
    return merged;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    localStorage.setItem('scenario_os_token', data.access_token);
    const profile = await authAPI.getProfile();
    const merged = {
      ...profile.data,
      password_change_required: Boolean(
        profile.data.password_change_required ?? data.password_change_required,
      ),
    };
    localStorage.setItem('scenario_os_learner', JSON.stringify(merged));
    setLearner(merged);
    return merged;
  }, []);

  const changePassword = useCallback(async (currentPassword, newPassword) => {
    try {
      const { data } = await authAPI.changePassword(currentPassword, newPassword);
      if (data?.access_token) {
        localStorage.setItem('scenario_os_token', data.access_token);
      }
      await refreshProfile();
      return { success: true };
    } catch (err) {
      const d = err?.response?.data?.detail;
      const msg = typeof d === 'string' ? d : (err?.message || 'Password change failed');
      return { success: false, error: msg };
    }
  }, [refreshProfile]);

  // ── Idle session timeout ─────────────────────────────────────────────────
  // Default: 30 min idle → logout, 2 min warning.
  // Override via GET /api/v1/platform/session-config?portal=learner
  const [idleTimeoutMs, setIdleTimeoutMs] = useState(30 * 60 * 1000);
  const [warningMs, setWarningMs]         = useState(2  * 60 * 1000);

  useEffect(() => {
    fetch('/api/v1/platform/session-config?portal=learner')
      .then((r) => r.ok ? r.json() : null)
      .catch(() => null)
      .then((cfg) => {
        if (cfg?.idle_timeout_ms)   setIdleTimeoutMs(cfg.idle_timeout_ms);
        if (cfg?.warning_before_ms) setWarningMs(cfg.warning_before_ms);
      });
  }, []);

  const { isWarning, countdown, extendSession } = useIdleTimeout({
    timeoutMs:  idleTimeoutMs,
    warningMs:  warningMs,
    onTimeout:  logout,
    enabled:    isAuthenticated,
  });
  // ─────────────────────────────────────────────────────────────────────────

  return (
    <>
      <SessionTimeoutWarning
        isWarning={isWarning}
        countdown={countdown}
        onExtend={extendSession}
        onLogout={logout}
        portalName="TeachIQ"
      />
      <AuthContext.Provider value={{
      learner,
      isAuthenticated,
      isCoach,
      isAdmin,
      isOwner,
      isPlatformAdmin,
      tenantId,
      enrolledDomains,
      authoringDomains,
      domainTiers,
      canAuthorDomain,
      isEnrolledIn,
      getTier,
      isPaidFor,
      loading,
      login,
      register,
      logout,
      refreshProfile,
      refreshToken,
      changePassword,
      activeCharacter,
      setActiveCharacter,
      activeSpecialty,
      setActiveSpecialty,
    }}>
        {children}
      </AuthContext.Provider>
    </>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
